import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

export const THEME_STORAGE_KEY = "zonify-theme";

const ThemeContext = createContext(null);

const getSystemTheme = () => {
  if (typeof window === "undefined" || !window.matchMedia) return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
};

export const applyThemePreference = (preference) => {
  const selectedPreference = ["light", "dark", "system"].includes(preference)
    ? preference
    : "system";
  const effectiveTheme = selectedPreference === "system" ? getSystemTheme() : selectedPreference;
  const root = document.documentElement;

  root.classList.toggle("dark", effectiveTheme === "dark");
  root.dataset.theme = effectiveTheme;
  root.dataset.themePreference = selectedPreference;
  root.style.colorScheme = effectiveTheme;

  return effectiveTheme;
};

export const initTheme = () => {
  const savedTheme = localStorage.getItem(THEME_STORAGE_KEY) || "system";
  applyThemePreference(savedTheme);
};

export const ThemeProvider = ({ children }) => {
  const [themePreference, setThemePreference] = useState(
    () => localStorage.getItem(THEME_STORAGE_KEY) || "system"
  );
  const [effectiveTheme, setEffectiveTheme] = useState(() => applyThemePreference(themePreference));

  useEffect(() => {
    localStorage.setItem(THEME_STORAGE_KEY, themePreference);
    setEffectiveTheme(applyThemePreference(themePreference));
  }, [themePreference]);

  useEffect(() => {
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const handleSystemThemeChange = () => {
      if (themePreference === "system") {
        setEffectiveTheme(applyThemePreference("system"));
      }
    };

    media.addEventListener("change", handleSystemThemeChange);
    return () => media.removeEventListener("change", handleSystemThemeChange);
  }, [themePreference]);

  const value = useMemo(
    () => ({
      themePreference,
      effectiveTheme,
      setThemePreference,
      isDark: effectiveTheme === "dark",
    }),
    [themePreference, effectiveTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return context;
};
